import ChangeStatusField from '@/components/common/change-status-field/change-status-field';
import CustomBackdrop from '@/components/common/custom-backdrop/custom-backdrop';
import CustomBlock from '@/components/common/custom-block/custom-block';
import FilterBox from '@/components/common/filters/common/filter-box';
import CustomListStylesContainer from '@/components/common/list-container/custom-list-styles-container';
import DisorderTreatmentActionField from '@/components/disorders-treatment/disorder-treatment-action-field';
import DisordersTreatmentListActions from '@/components/disorders-treatment/disorders-treatment-list-actions';
import DisordersTreatmentListBulkActionsButtons from '@/components/disorders-treatment/disorders-treatment-list-bulk-actions-buttons';
import {
    AVATARS,
    AVATAR_GROUP_QUOTE_TYPE,
    CURATIVE_AVATARS_NAMES,
    CURATIVE_CHOICES,
    DISORDER_CHANGE_STATUS_NODE_LIST,
    DISORDER_METADATA_AVATAR_TYPE_NAME,
    DISORDER_TYPE_NAME_VALUES,
    GROUP_AVATARS,
    METADATA_AVATAR_TYPES,
    ROLE_BIG_CREATE_QUOTE,
    ROLE_BIG_DELETE_DEMAND,
    ROLE_BIG_READ_DEMAND,
    ROLE_BIG_UPDATE_DEMAND_STATUS,
    TREATMENT_AVATARS_NAMES,
    TREATMENT_CHOICES,
    TREATMENT_METADATA_AVATAR_TYPE_NAME,
} from '@/utils/CONST';
import tableRowSxWithNodeStatusVerification from '@/utils/table-row-sx-with-node-status-verification';
import Box from '@mui/material/Box';
import { last, map, split } from 'lodash';
import { useState } from 'react';
import {
    ChipField,
    Datagrid,
    DateField,
    DateInput,
    FunctionField,
    Identifier,
    List,
    ReferenceArrayField,
    ReferenceField,
    SelectInput,
    SingleFieldList,
    TextField,
    useAuthenticated,
    usePermissions,
    useRecordSelection,
    useTranslate,
    useUnselectAll,
} from 'react-admin';
import { useParams } from 'react-router';

const DisordersTreatmentList = () => {
    useAuthenticated();

    const translate = useTranslate();
    const { placeId } = useParams();
    const [selectedAvatarsIds, { select, unselect }] = useRecordSelection({
        resource: AVATARS,
        storeKey: 'disordersTreatmentList',
    });

    const unselectAll = useUnselectAll(AVATARS, 'disordersTreatmentList');
    const [multiSelectionState, setMultiSelectionState] = useState<boolean>(false);
    const [customBackdropLoadingState, setCustomBackdropLoadingState] = useState<boolean>(false);
    const { permissions } = usePermissions();
    const [action, setAction] = useState<'create_quote' | 'status' | undefined>();

    const onRowClick: any = (id: Identifier, _resource: string, record: any) => {
        if (multiSelectionState) {
            if (action === 'create_quote' && record?.currentNode?.configuration?.idForNodeHumanReadable === '0010') {
                if (selectedAvatarsIds.includes(id)) {
                    unselect([id]);
                } else {
                    select([...selectedAvatarsIds, id]);
                }
            } else {
                return false;
            }
        } else {
            unselectAll();
            select([id]);
        }
    };

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: 1,
                gridTemplateRows: '1fr',
                height: '100%',
                overflowY: 'auto',
            }}
        >
            <CustomBlock label={translate('main.navigation.disorder')}>
                <CustomListStylesContainer>
                    <List
                        actions={
                            <DisordersTreatmentListActions
                                action={action}
                                setAction={setAction}
                                multiSelectionState={multiSelectionState}
                                setMultiSelectionState={setMultiSelectionState}
                            />
                        }
                        component='div'
                        resource={AVATARS}
                        filter={{
                            metadataAvatarTypeEName: [
                                TREATMENT_METADATA_AVATAR_TYPE_NAME,
                                DISORDER_METADATA_AVATAR_TYPE_NAME,
                            ],
                            parent: placeId,
                            serialization_groups: ['read_avatar_current_node', 'read_node'],
                            enabled: true,
                        }}
                        empty={false}
                        storeKey='disordersTreatmentList'
                        filters={filters(translate)}
                        sort={{
                            field: 'creationDate',
                            order: 'DESC',
                        }}
                        queryOptions={{
                            enabled: permissions?.includes(ROLE_BIG_READ_DEMAND),
                        }}
                    >
                        <Datagrid
                            rowClick={onRowClick}
                            rowSx={tableRowSxWithNodeStatusVerification(
                                selectedAvatarsIds,
                                multiSelectionState,
                                action,
                                '0010',
                            )}
                            bulkActionButtons={
                                (permissions?.includes(ROLE_BIG_UPDATE_DEMAND_STATUS) ||
                                    permissions?.includes(ROLE_BIG_CREATE_QUOTE)) ? (
                                    multiSelectionState ? (
                                        <DisordersTreatmentListBulkActionsButtons
                                            action={action}
                                            setAction={setAction}
                                            setCustomBackdropLoadingState={setCustomBackdropLoadingState}
                                            setMultiSelectionState={setMultiSelectionState}
                                        />
                                    ) : (
                                        false
                                    )
                                ) : (
                                    false
                                )
                            }
                            isRowSelectable={(record) =>
                                !(
                                    multiSelectionState &&
                                    action === 'create_quote' &&
                                    record?.currentNode?.configuration?.idForNodeHumanReadable !== '0010'
                                )
                            }
                        >
                            <FunctionField
                                render={({ alphaId }: { alphaId: string }) => split(alphaId, ':')[1]}
                                label={'disorder.list.number'}
                            />

                            <ReferenceField
                                source='metadataAvatarType'
                                reference={METADATA_AVATAR_TYPES}
                                label={'disorder.list.type'}
                            >
                                {/*<TextField source={'name'} />*/}
                                <FunctionField
                                    render={({ name }: { name: string }) =>
                                        translate(`disorder.list.${last(split(name, ':'))}`)
                                    }
                                />
                            </ReferenceField>

                            <TextField source='name' label='disorder.list.subtype' />

                            <DateField
                                options={{ year: 'numeric', month: '2-digit', day: '2-digit' }}
                                source={'creationDate'}
                                label={'disorder.list.creation_date'}
                            />

                            {permissions?.includes(ROLE_BIG_UPDATE_DEMAND_STATUS) ? (
                                <FunctionField
                                    render={({ currentNode, id }: any) => (
                                        <ChangeStatusField
                                            avatarUriId={id}
                                            currentNode={currentNode}
                                            choices={Object.values(DISORDER_CHANGE_STATUS_NODE_LIST)}
                                        />
                                    )}
                                    label='intervention.list.status'
                                />
                            ) : (
                                <TextField source='currentNode.name' label='intervention.list.status' />
                            )}

                            <DateField
                                sortable={false}
                                options={{ year: 'numeric', month: '2-digit', day: '2-digit' }}
                                source={'currentNode.dateOfTakesPlace'}
                                label='intervention.list.status_date'
                            />

                            <ReferenceArrayField
                                reference={GROUP_AVATARS}
                                source='groupAvatars'
                                label='disorder.list.present_devis'
                                sx={{
                                    '& > span > div': {
                                        display: 'flex',
                                        gap: 0.2,
                                    },
                                }}
                                filter={{ type: AVATAR_GROUP_QUOTE_TYPE }}
                            >
                                <SingleFieldList linkType={false}>
                                    <ChipField source='name' />
                                </SingleFieldList>
                            </ReferenceArrayField>

                            <FunctionField
                                render={(avatar: any) => (
                                    <DisorderTreatmentActionField
                                        avatar={avatar}
                                        withDelete={permissions?.includes(ROLE_BIG_DELETE_DEMAND)}
                                    />
                                )}
                            />
                        </Datagrid>
                    </List>
                </CustomListStylesContainer>
            </CustomBlock>

            {customBackdropLoadingState && <CustomBackdrop />}
        </Box>
    );
};

const filters = (translate: any) => [
    <FilterBox source={'filters1'} label={'intervention.filters.label'} alwaysOn>
        <SelectInput
            source='metadataAvatarType.name'
            name='metadataAvatarType.name'
            label='disorder.filters.type'
            sx={{
                width: 200,
            }}
            choices={map(DISORDER_TYPE_NAME_VALUES, (v) => ({
                name: translate(`disorder.list.${v}`),
                id: `big:type:${v}`,
            }))}
            alwaysOn
            resettable
        />

        <SelectInput
            source='name'
            name='name'
            label='disorder.filters.subtype'
            sx={{
                width: 200,
            }}
            choices={[
                ...map(CURATIVE_CHOICES, (v) => ({
                    name: `${translate('disorder.list.disorder')}/ ${CURATIVE_AVATARS_NAMES[v]}`,
                    id: CURATIVE_AVATARS_NAMES[v],
                })),
                ...map(TREATMENT_CHOICES, (v) => ({
                    name: `${translate('disorder.list.treatment')}/ ${TREATMENT_AVATARS_NAMES[v]}`,
                    id: TREATMENT_AVATARS_NAMES[v],
                })),
            ]}
            alwaysOn
            resettable
        />
    </FilterBox>,

    <FilterBox source={'filters2'} label={'disorder.filters.period'} alwaysOn>
        <DateInput
            source='creationDate__after'
            name='creationDate__after'
            label='disorder.filters.from'
            sx={{
                width: 200,
            }}
            alwaysOn
        />

        <DateInput
            source='creationDate__before'
            name='creationDate__before'
            label='disorder.filters.to'
            sx={{
                width: 200,
            }}
            alwaysOn
        />
    </FilterBox>,

    <FilterBox source={'filters3'} label={'intervention.filters.label'} alwaysOn>
        <SelectInput
            source={'currentNode.name'}
            name={'currentNode.name'}
            choices={map(Object.values(DISORDER_CHANGE_STATUS_NODE_LIST), ({ name }) => ({
                name: translate(name),
                id: translate(name),
            }))}
            label='intervention.filters.status'
            sx={{
                width: 200,
            }}
            alwaysOn
            resettable
        />
    </FilterBox>,
];

export default DisordersTreatmentList;
